// ─────────────────────────────────────────────
//  WeightCostCalculator.jsx — Peso vs presupuesto
//  Referencia + sliders acoplados + resultado visual
// ─────────────────────────────────────────────

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import appStyles from '../layout/CalculatorPage.module.css';
import { KEYS, getSetting } from '../../utils/storage';
import { DEFAULTS, REF_OPTIONS } from '../../utils/defaults';
import ReferenceCard from './ReferenceCard';
import LinkedSliders from './LinkedSliders';
import WeightResult from './WeightResult';

const BUDGET_MIN = 10;
const BUDGET_MAX = 1000;
const WEIGHT_MIN = 100;
const WEIGHT_MAX = 5000;

const BUDGET_QUICK_OPTIONS = [
	{ id: 'b50', value: 50, label: '$50' },
	{ id: 'b100', value: 100, label: '$100' },
	{ id: 'b150', value: 150, label: '$150' },
	{ id: 'b200', value: 200, label: '$200' },
	{ id: 'b500', value: 500, label: '$500' },
];

const WEIGHT_QUICK_OPTIONS = [
	{ id: 'w250', value: 250, label: '1/4 kg' },
	{ id: 'w500', value: 500, label: '1/2 kg' },
	{ id: 'w750', value: 750, label: '3/4 kg' },
	{ id: 'w1000', value: 1000, label: '1 kg' },
	{ id: 'w1500', value: 1500, label: '1.5 kg' },
];

const DEFAULT_REF_PRICE = '';
const DEFAULT_BUDGET = 80;

function getDefaultRefGrams() {
	return getSetting(KEYS.defaultRefGrams, DEFAULTS.defaultRefGrams);
}

export default function WeightCostCalculator() {
	const [refGrams, setRefGrams] = useState(getDefaultRefGrams);
	const [refPrice, setRefPrice] = useState(DEFAULT_REF_PRICE);
	const [budget, setBudget] = useState(DEFAULT_BUDGET);
	const [weightGrams, setWeightGrams] = useState(416);
	const [driver, setDriver] = useState('budget');

	const parsedRefPrice = parseFloat(refPrice) || 0;
	const hasReference = parsedRefPrice > 0 && refGrams > 0;

	const pricePerGram = useMemo(
		() => (hasReference ? parsedRefPrice / refGrams : 0),
		[hasReference, parsedRefPrice, refGrams]
	);

	const syncFromBudget = useCallback((nextBudget) => {
		const clamped = Math.min(BUDGET_MAX, Math.max(BUDGET_MIN, Math.round(nextBudget)));
		setBudget(clamped);
		if (hasReference) {
			const nextWeight = Math.round((clamped * refGrams) / parsedRefPrice);
			setWeightGrams(Math.min(WEIGHT_MAX, Math.max(WEIGHT_MIN, nextWeight)));
		}
	}, [hasReference, parsedRefPrice, refGrams]);

	const syncFromWeight = useCallback((nextWeight) => {
		const clamped = Math.min(WEIGHT_MAX, Math.max(WEIGHT_MIN, Math.round(nextWeight)));
		setWeightGrams(clamped);
		if (hasReference) {
			const nextBudget = Math.round((clamped * parsedRefPrice) / refGrams);
			setBudget(Math.min(BUDGET_MAX, Math.max(BUDGET_MIN, nextBudget)));
		}
	}, [hasReference, parsedRefPrice, refGrams]);

	const handleRefGramsChange = useCallback((grams) => {
		setRefGrams(grams);
	}, []);

	const handleRefPriceChange = useCallback((value) => {
		setRefPrice(value);
	}, []);

	useEffect(() => {
		if (!hasReference) return;
		if (driver === 'weight') {
			const nextBudget = Math.round((weightGrams * parsedRefPrice) / refGrams);
			setBudget(Math.min(BUDGET_MAX, Math.max(BUDGET_MIN, nextBudget)));
		} else {
			const nextWeight = Math.round((budget * refGrams) / parsedRefPrice);
			setWeightGrams(Math.min(WEIGHT_MAX, Math.max(WEIGHT_MIN, nextWeight)));
		}
	}, [parsedRefPrice, refGrams, hasReference]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleBudgetChange = useCallback((value) => {
		setDriver('budget');
		syncFromBudget(value);
	}, [syncFromBudget]);

	const handleWeightChange = useCallback((value) => {
		setDriver('weight');
		syncFromWeight(value);
	}, [syncFromWeight]);

	const handleReset = useCallback(() => {
		setRefGrams(getDefaultRefGrams());
		setRefPrice(DEFAULT_REF_PRICE);
		setBudget(DEFAULT_BUDGET);
		setWeightGrams(416);
		setDriver('budget');
	}, []);

	return (
		<main className={appStyles.root} aria-label="Calculadora de peso y costo">

			<ReferenceCard
				options={REF_OPTIONS}
				refGrams={refGrams}
				refPrice={refPrice}
				onRefGramsChange={handleRefGramsChange}
				onRefPriceChange={handleRefPriceChange}
				pricePerGram={pricePerGram}
				hasReference={hasReference}
				onReset={handleReset}
			/>

			<LinkedSliders
				budget={budget}
				weightGrams={weightGrams}
				onBudgetChange={handleBudgetChange}
				onWeightChange={handleWeightChange}
				budgetMin={BUDGET_MIN}
				budgetMax={BUDGET_MAX}
				weightMin={WEIGHT_MIN}
				weightMax={WEIGHT_MAX}
				disabled={!hasReference}
				budgetQuickOptions={BUDGET_QUICK_OPTIONS}
				weightQuickOptions={WEIGHT_QUICK_OPTIONS}
			/>

			<WeightResult
				weightGrams={weightGrams}
				budget={budget}
				hasReference={hasReference}
				pricePerGram={pricePerGram}
				weightMin={WEIGHT_MIN}
				weightMax={WEIGHT_MAX}
			/>

		</main>
	);
}
